import {Fragment} from 'react';
import type {SxProps} from '@mui/material';
import {useTheme} from '@mui/material';
import type {IconName} from 'components/icons/types';
import type {NodeAndAncestors} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {Icon} from 'components/icons';
import {Layout} from 'components/layout';
import {Text, TextMatch} from 'components/text';

type Props = {
  node: NodeAndAncestors;
  prefixIcon?: IconName;
  code: string | undefined;
  /** Show category level, default false */
  showCategory?: boolean;
  /** Will highlight any matches in code and node label if set */
  searchQuery?: string;
  pathSx?: SxProps;
};

const separatorSx = {
  // fontWeight: 'bold',
  px: 1,
};
const Separator = () => (
  <Text variant="body2" sx={separatorSx}>
    {'/'}
  </Text>
);

const sxCode = {
  pr: 0.5,
};

export const ThesaurusNodePath: React.FC<Props> = ({
  node,
  prefixIcon,
  code,
  showCategory = false,
  searchQuery,
  pathSx,
}) => {
  const {t} = useLocalization();
  const {colors} = useTheme();

  const sxAlpha75: SxProps = {
    color: colors.burgundy75,
  };

  const sxAlpha50: SxProps = {
    color: colors.burgundy50,
  };

  const sxCodeDeactivated: SxProps = {
    color: colors.burgundy50,
    pr: 0.5,
  };

  const {
    node: {label, deactivated},
    ancestors,
  } = node;

  return (
    <Layout horizontal adjustCenter adjustLeft wrap sx={pathSx}>
      {prefixIcon ? <Icon icon={prefixIcon} /> : null}
      {code ? (
        <TextMatch
          match={searchQuery}
          sx={deactivated ? sxCodeDeactivated : sxCode}
          variant="body2">{`(${code})`}</TextMatch>
      ) : null}
      {ancestors.slice(showCategory ? 1 : 2).map(ancestor => (
        <Fragment key={ancestor.id}>
          <Text variant="body2" sx={deactivated ? sxAlpha50 : sxAlpha75}>
            {ancestor.label}
          </Text>
          <Separator />
        </Fragment>
      ))}
      <TextMatch
        match={searchQuery}
        variant="body2"
        sx={deactivated ? sxAlpha50 : undefined}>
        {deactivated ? `${label} (${t('general.deactivated')})` : label}
      </TextMatch>
    </Layout>
  );
};
